import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  AvailableEnergyPointReport,
  RequestReportInterFace,
} from '../../../../interface/reportenergyPoint.interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import {
  fetchEnergyReportPoint,
  updateReportFilters,
  setReportFiltersData,
  fetchListFilters,
  setEnergyPointReportUpdate,
  setSortData,
} from '../../../../redux/slices/ReportEnergyPointSlice';
import { resetReportUpdateFlag } from '../../../../redux/slices/ReportSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import { useTableSidePanel } from '../../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import StationSidePanel from '../../../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import reportApi from '../../../../utils/api/reports';

interface ReportTableProperties {
  leftTopContent?: JSX.Element;
  preferenceFilter: any;
}

/**
 * EnergyReportTablePoint component
 * @param {any} properties - The properties
 */
function EnergyReportTablePoint(properties: ReportTableProperties) {
  const { t } = useTranslation('report');
  const { leftTopContent, preferenceFilter } = properties;
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const {
    reports,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filters,
    filterListLoading,
    isFilterUpdated,
    isEnergyPointReportUpdate,
    sortBy,
    sortField,
  } = useAppSelector((state) => state.reportEnergyPoint);
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  /**
   * Fetches energy report data for the selected facility.
   * @param {any} data - The request parameters for fetching the report.
   */
  const fetchData = (data: RequestReportInterFace) => {
    if (!loading) {
      dispatch(
        fetchEnergyReportPoint({
          ...data,
          accountCustomId: reportsData?.accountId,
        }),
      );
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchEnergyReportPoint({ ...queryParameters, page, limit, sortField, sortBy });
  };
  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: reports,
      disableEdit: true,
      disableDelete: true,
    });
  const column = [
    {
      label: t('chargingPoint'),
      accessorKey: 'chargingPointSn' as keyof AvailableEnergyPointReport,
      queryParamsKey: 'deviceId',
      queryParamsValue: 'chargingPointSn',
      sidePanelComponent: (
        <DeviceSidePanel
          fetchIdFrom="queryParams"
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('stationInfo'),
      accessorKey: 'chargingStationName' as keyof AvailableEnergyPointReport,
      sidePanelComponent: (
        <StationSidePanel
          fetchIdFrom="queryParams"
          initialIsEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          onSuccess={() => {
            handleEditModeChange(false);
            handleRefresh();
          }}
        />
      ),
      disableNewTab: true,
      /**
       * S.No view
       * @param {any} report - The report data
       */ customData: (report: AvailableEnergyPointReport) => (
        <div className="table-row-each-data">
          <div className="station-info-left">
            <span role="presentation">{report.chargingStationName}</span>
            <span>{report.date}</span>
          </div>
          <div className="station-info-right">
            <span role="presentation">{report.chargingStationLocation}</span>
          </div>
        </div>
      ),
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sortAllow: false,
      columnWidth: 204,
    },

    {
      label: t('powerConsumed'),
      accessorKey: 'powerConsumed' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>{ToFixedValue(row?.powerConsumed,3)}</span>
      ),
    },
    {
      label: t('basePricingEnergyConsumption'),
      accessorKey: 'basePricePowerConsumed' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>{ToFixedValue(row?.basePricePowerConsumed,3)}</span>
      ),
    },

    {
      label: t('peakPricingEnergyConsumption'),
      accessorKey: 'peakPricePowerConsumed' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>{ToFixedValue(row?.peakPricePowerConsumed,3)}</span>
      ),
    },
    {
      label: t('offPeakPricingEnergyConsumption'),
      accessorKey: 'offPeakPricePowerConsumed' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>{ToFixedValue(row?.offPeakPricePowerConsumed,3)}</span>
      ),
    },
    {
      label: t('scheduleChargeEnergyConsumption'),
      accessorKey: 'scheduledPowerConsumed' as keyof AvailableEnergyPointReport,
      sortAllow: true,
      /**
       * decimal value round of
       * @param {any} row - The row
       * @returns {any} The result
       */
      customData: (row: AvailableEnergyPointReport) => (
        <span>{ToFixedValue(row?.scheduledPowerConsumed,3)}</span>
      ),
    },
  ];

  /**
   * Reference Current Pages
   */
  const ReferenceCurrentPages = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortBy,
      sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page,
      limit,
    });
  };

  /**
   * Current columns for Energy Point Report export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('date'), accessorKey: 'date' },
    { label: t('stationName'), accessorKey: 'chargingStationName' },
    { label: t('stationLocation'), accessorKey: 'chargingStationLocation' },
    { label: t('powerConsumed'), accessorKey: 'powerConsumed' },
    { label: t('basePricingEnergyConsumption'), accessorKey: 'basePricePowerConsumed' },
    { label: t('peakPricingEnergyConsumption'), accessorKey: 'peakPricePowerConsumed' },
    { label: t('offPeakPricingEnergyConsumption'), accessorKey: 'offPeakPricePowerConsumed' },
    { label: t('scheduleChargeEnergyConsumption'), accessorKey: 'scheduledPowerConsumed' },
  ];
  const exportCurrentColumns = currentColumns.filter((col) => {
    // Always show serial number
    if (col.accessorKey === '__serial__') return true;

    // 🔥 If chargingStationName is hidden,
    // automatically hide related columns
    const isParentHidden = hiddenColumns.includes('chargingStationName');

    if (isParentHidden) {
      if (
        col.accessorKey === 'chargingStationName' ||
        col.accessorKey === 'date' ||
        col.accessorKey === 'chargingStationLocation'
      ) {
        return false;
      }
    }

    // Default hide logic
    return !hiddenColumns.includes(col.accessorKey);
  });
  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);

    const exportParameters: any = {
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
    };

    if (sortBy) {
      exportParameters.sortBy = sortBy;
      exportParameters.sortField = sortField;
    }

    reportApi
      .fetchEnergicPointListExport({
        ...exportParameters,
        accountCustomId: reportsData?.accountId,
      })
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: AvailableEnergyPointReport, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'date':
                return row?.date || '-';

              case 'chargingStationName':
                return row?.chargingStationName || '-';

              case 'chargingStationLocation':
                return row?.chargingStationLocation || '-';

              case 'powerConsumed':
                return row?.powerConsumed ? ToFixedValue(row.powerConsumed,3) : '0.000';

              case 'basePricePowerConsumed':
                return row?.basePricePowerConsumed
                  ? ToFixedValue(Number(row.basePricePowerConsumed),3)
                  : '0.000';

              case 'peakPricePowerConsumed':
                return row?.peakPricePowerConsumed
                  ? ToFixedValue(Number(row.peakPricePowerConsumed),3)
                  : '0.000';

              case 'offPeakPricePowerConsumed':
                return row?.offPeakPricePowerConsumed
                  ? ToFixedValue(row.offPeakPricePowerConsumed,3)
                  : '0.000';

              case 'scheduledPowerConsumed':
                return row?.scheduledPowerConsumed
                  ? ToFixedValue(row.scheduledPowerConsumed,3)
                  : '0.000';

              default:
                return '-';
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 404,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to handle ApiRequest when page or limit changes
   * @param {any} value - The request parameters
   */
  const handleApiRequest = (value: RequestReportInterFace) => {
    fetchData({
      ...GetParametersFromCommonFilters(filters),
      ...value,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      sortField,
      sortBy,
    });
  };

  const fetchReportData = useCallback(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      startDate: reportsData.reportFilter.startDate,
      endDate: reportsData.reportFilter.endDate,
      page: DefaultApiLimit.page,
      limit: DefaultApiLimit.limit,
    });
  }, [
    filters,
    reportsData.reportFilter.startDate,
    reportsData.reportFilter.endDate,
    reportsData.reportFilter.year,
  ]);

  useEffect(() => {
    if (isFilterUpdated || reports.length === 0 || isEnergyPointReportUpdate) {
      fetchReportData();
      dispatch(resetReportUpdateFlag());
      dispatch(setEnergyPointReportUpdate(false));
    }
  }, [fetchReportData, isFilterUpdated, reports.length, isEnergyPointReportUpdate]);

  useEffect(() => {
    if (
      preferenceFilter.pageName === 'energyReport' &&
      filters.length > 1 &&
      reportsData.reportByType !== 'station'
    ) {
      const setFilter = filters.map((filter) => {
        let returnValue = filter;
        const single = preferenceFilter.filter?.find(
          (preference: any) => preference.field === filter.key,
        );
        if (single && single?.value.length > 0) {
          returnValue = {
            ...returnValue,
            value: single?.value,
            applyFilter: true,
            valueToShow: returnValue?.listOfValue
              ?.filter((show) => single?.value?.includes(show.value))
              .map((show) => show.label),
          };
        } else {
          returnValue = {
            ...returnValue,
            value: [],
            applyFilter: false,
            valueToShow: [],
          };
        }
        return returnValue;
      });
      dispatch(setReportFiltersData(setFilter));
      const queryParameters = GetParametersFromCommonFilters(setFilter);
      fetchData({
        ...queryParameters,
        startDate: reportsData.reportFilter.startDate,
        endDate: reportsData.reportFilter.endDate,
        page: DefaultApiLimit.page,
        limit,
      });
    }
  }, [preferenceFilter]);

  useEffect(() => {
    if (filters.length <= 1 && reportsData?.accountId) {
      dispatch(fetchListFilters({ accountCustomId: reportsData?.accountId }));
    }
  }, [reportsData?.accountId]);

  return (
    <div>
      <CustomTable
        disablePagination={false}
        loading={loading}
        limit={limit}
        header={column}
        data={reports}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        pageLimitChange={handleApiRequest}
        version="v2"
        exportDataPdfOrCSV={
          permissionCheck('mobilityEnergyReportExport') ? exportDataPdfOrCSV : undefined
        }
        exportData={reports}
        exportLoader={exportLoader}
        disableHeader={false}
        filters={filters}
        height={370}
        filtersToDispatch={updateReportFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchListFilters}
        leftTopContent={leftTopContent}
        refershCurrentPages={ReferenceCurrentPages}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        sizeChangeButtonRequired
        serialNo
        onHiddenColumnsChange={setHiddenColumns}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
      />
    </div>
  );
}

export default EnergyReportTablePoint;
